import React, { useState, useEffect } from 'react';
import { useDashboard } from '../../contexts/DashboardContext';
import TrafficSourceFilter from '../TrafficSourceFilter';
import UserIdFilter from '../UserIdFilter';
import TimePicker from '../TimePicker';
import { normalizeStartDate, normalizeEndDate, getDatePreset } from '../../utils/dateUtils';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarIcon, Users, Film, Heart, Target, Layers } from 'lucide-react';
import { cn } from '../../lib/utils';

// Конфигурация проектов (синхронизирована с сервером)
const PROJECTS = [
  { id: 'shortly', name: 'Shortly', description: 'Основной проект' },
  { id: 'novellas', name: 'Shortly Novellas', description: 'Новеллы' },
];

const Sidebar = () => {
  const {
    projectId,
    setProjectId,
    activeSection,
    setActiveSection,
    startDate,
    endDate,
    startTime,
    endTime,
    updateDates,
    updateTimes,
    resetDates,
    updateTrafficSource,
    trafficSource,
    userIdFilter,
    updateUserIdFilter
  } = useDashboard();

  // Локальные состояния для временных дат
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  
  // Локальные состояния для временного времени
  const [tempStartTime, setTempStartTime] = useState({ hours: 0, minutes: 0 });
  const [tempEndTime, setTempEndTime] = useState({ hours: 23, minutes: 59 });
  
  // Состояние для выбранных кампаний
  const [tempSelectedCampaigns, setTempSelectedCampaigns] = useState([]);
  
  // Временное состояние для фильтра по ID пользователей
  const [tempUserIdFilter, setTempUserIdFilter] = useState('all');
  
  // Состояния для управления открытием календарей
  const [startDatePopoverOpen, setStartDatePopoverOpen] = useState(false);
  const [endDatePopoverOpen, setEndDatePopoverOpen] = useState(false);

  // Инициализация дат при первой загрузке
  useEffect(() => {
    if (!startDate && !endDate) {
      // Устанавливаем последние 30 дней по умолчанию
      const { start, end } = getDatePreset('last30days');
      setTempStartDate(start);
      setTempEndDate(end);
    } else {
      // Нормализуем существующие даты
      setTempStartDate(startDate ? normalizeStartDate(startDate) : null);
      setTempEndDate(endDate ? normalizeEndDate(endDate) : null);
    }
  }, [startDate, endDate]);

  // Инициализация времени при первой загрузке
  useEffect(() => {
    setTempStartTime(startTime);
    setTempEndTime(endTime);
  }, [startTime, endTime]);

  // Инициализация фильтра по ID пользователей при первой загрузке
  useEffect(() => {
    setTempUserIdFilter(userIdFilter);
  }, [userIdFilter]);

  const sections = [
    { id: 'users', name: 'Пользователи', icon: Users },
    { id: 'series', name: 'Сериалы', icon: Film },
    { id: 'pulse', name: 'Пульс', icon: Heart },
    { id: 'showcase', name: 'Витрина', icon: Target }
  ];

  const handleDateChange = (type, date) => {
    if (!date) {
      if (type === 'start') {
        setTempStartDate(null);
      } else {
        setTempEndDate(null);
      }
      return;
    }
    
    // ВСЕГДА нормализуем даты
    if (type === 'start') {
      setTempStartDate(normalizeStartDate(date));
      setStartDatePopoverOpen(false); // Закрываем попап после выбора даты
    } else {
      setTempEndDate(normalizeEndDate(date));
      setEndDatePopoverOpen(false); // Закрываем попап после выбора даты
    }
  };

  const handleApply = () => {
    // Применяем даты
    updateDates(tempStartDate, tempEndDate);
    
    // Применяем время
    updateTimes(tempStartTime, tempEndTime);
    
    // Применяем кампании
    updateTrafficSource({
      campaign: 'all',
      campaigns: tempSelectedCampaigns,
      mediaSource: 'all'
    });
    
    // Применяем фильтр по ID пользователей
    updateUserIdFilter(tempUserIdFilter);
  };

  // Обработчик для получения выбранных кампаний из TrafficSourceFilter
  const handleCampaignsChange = (campaigns) => {
    setTempSelectedCampaigns(campaigns);
  };

  // Пресеты дат - используем готовую функцию с нормализацией
  const setPreset = (preset) => {
    const { start, end } = getDatePreset(preset);
    setTempStartDate(start);
    setTempEndDate(end);
    
    // Устанавливаем время от 00:00 до 23:59 для всех пресетов
    setTempStartTime({ hours: 0, minutes: 0 });
    setTempEndTime({ hours: 23, minutes: 59 });
  };

  // Сдвиг дат на день с нормализацией
  const shiftDates = (direction) => {
    if (!tempStartDate || !tempEndDate) return;

    const shift = direction === 'forward' ? 1 : -1;
    const newStart = new Date(tempStartDate);
    const newEnd = new Date(tempEndDate);
    
    newStart.setDate(newStart.getDate() + shift);
    newEnd.setDate(newEnd.getDate() + shift);
    
    // Нормализуем после сдвига
    setTempStartDate(normalizeStartDate(newStart));
    setTempEndDate(normalizeEndDate(newEnd));
    
    // Устанавливаем время от 00:00 до 23:59 при сдвиге дат
    setTempStartTime({ hours: 0, minutes: 0 });
    setTempEndTime({ hours: 23, minutes: 59 });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Верхняя часть с фиксированным контентом */}
      <div className="flex-shrink-0 p-4 space-y-6">
        {/* Переключатель проектов */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Проект</h3>
          </div>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-full h-9">
              <SelectValue placeholder="Выберите проект" />
            </SelectTrigger>
            <SelectContent>
              {PROJECTS.map(project => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex flex-col">
                    <span>{project.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Навигация */}
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-1">
            {sections.map(section => {
              const IconComponent = section.icon;
              return (
                <Button
                  key={section.id}
                  variant={activeSection === section.id ? "secondary" : "ghost"}
                  size="sm"
                  className="h-9 text-sm justify-start px-3"
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  <span>{section.name}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Период */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Период</h3>
          </div>
          
          {/* Кнопки пресетов */}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={() => setPreset('today')} className="text-xs h-8">Сегодня</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('yesterday')} className="text-xs h-8">Вчера</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last3days')} className="text-xs h-8">3 дня</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last7days')} className="text-xs h-8">7 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last30days')} className="text-xs h-8">30 дней</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset('last90days')} className="text-xs h-8">90 дней</Button>
          </div>

          {/* Кнопки сдвига дат */}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => shiftDates('backward')} className="flex-1 text-xs h-8">← День</Button>
            <Button variant="outline" size="sm" onClick={() => shiftDates('forward')} className="flex-1 text-xs h-8">День →</Button>
          </div>
          
          {/* Поля дат и времени */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Popover open={startDatePopoverOpen} onOpenChange={setStartDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-9 w-full justify-start text-left font-normal text-xs",
                      !tempStartDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {tempStartDate ? (
                      tempStartDate.toLocaleDateString('ru-RU')
                    ) : (
                      "Начальная дата"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tempStartDate}
                    onSelect={(date) => handleDateChange('start', date)}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <TimePicker
                value={tempStartTime}
                onChange={setTempStartTime}
                placeholder="00:00"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Popover open={endDatePopoverOpen} onOpenChange={setEndDatePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "h-9 w-full justify-start text-left font-normal text-xs",
                      !tempEndDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {tempEndDate ? (
                      tempEndDate.toLocaleDateString('ru-RU')
                    ) : (
                      "Конечная дата"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={tempEndDate}
                    onSelect={(date) => handleDateChange('end', date)}
                    disabled={(date) => tempStartDate && date < tempStartDate}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <TimePicker
                value={tempEndTime}
                onChange={setTempEndTime}
                placeholder="23:59"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Фильтр по ID пользователей */}
        <UserIdFilter 
          value={tempUserIdFilter} 
          onChange={setTempUserIdFilter} 
        />

        <Separator />
      </div>

      {/* Растягивающаяся часть с фильтром кампаний */}
      <div className="flex-1 min-h-0 px-4">
        <TrafficSourceFilter onCampaignsChange={handleCampaignsChange} />
      </div>

      {/* Кнопка применить - зафиксирована внизу */}
      <div className="flex-shrink-0 p-4 border-t">
        <Button
          onClick={handleApply}
          className="w-full"
          disabled={!tempStartDate || !tempEndDate}
        >
          Применить
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;
